import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, SystemRole, OrganizationRole, UserManagementUser } from '../types';
import { isSuperAdmin, getEffectiveRole } from '../utils/roleUtils';
import { updateUserRole, getAllUsers } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, getDocs, doc, updateDoc, arrayUnion, arrayRemove, getDoc, deleteField } from 'firebase/firestore';

interface EditUserModalProps {
  userId: string;
  currentUser: User;
  onClose: () => void;
  onUserUpdated: () => void;
}

interface Organization {
  id: string;
  name: string;
}

interface Program {
  id: string;
  name: string;
  managers?: string[];
  managerNames?: string[];
}

interface Group {
  id: string;
  name: string;
  managers?: string[];
  managerNames?: string[];
}

export function EditUserModal({ userId, currentUser, onClose, onUserUpdated }: EditUserModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    systemRole: 'user' as SystemRole,
    organizationRole: 'admin' as OrganizationRole,
    organizationId: '',
    assignedPrograms: [] as string[],
    assignedGroups: [] as string[]
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [userToEditData, setUserToEditData] = useState<any>(null);

  useEffect(() => {
    loadUserData();
    loadOrganizations();
    loadPrograms();
    loadGroups();
  }, [userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const allUsers = await getAllUsers();
      const userToEdit = allUsers.find((u: any) => u.userId === userId);
      
      if (userToEdit) {
        setUserToEditData(userToEdit);
        
        // Load user's current program and group assignments
        const [userPrograms, userGroups] = await Promise.all([
          loadUserPrograms(userId),
          loadUserGroups(userId)
        ]);
        
        // Handle role conversion - check if using new structure or legacy
        // Use getEffectiveRole to properly handle both new and legacy formats
        const effectiveRole = getEffectiveRole({
          id: userToEdit.userId,
          role: userToEdit.role,
          organizationRole: userToEdit.organizationRole,
          organizationId: userToEdit.organizationId,
          legacyRole: userToEdit.legacyRole
        } as any);
        
        let systemRole: SystemRole = effectiveRole.systemRole;
        let organizationRole: OrganizationRole = effectiveRole.organizationRole || 'admin';
        
        // For super_admin, don't set organizationRole (it should be undefined)
        if (systemRole === 'super_admin') {
          organizationRole = 'admin' as OrganizationRole; // Set default for form, but it won't be saved
        }
        
        setFormData({
          name: userToEdit.name,
          email: userToEdit.email,
          systemRole: systemRole,
          organizationRole: organizationRole,
          organizationId: userToEdit.organizationId || '',
          assignedPrograms: userPrograms,
          assignedGroups: userGroups
        });
      } else {
        setError(t('editUser.userNotFound'));
      }
    } catch (err) {
      console.error('Error loading user:', err);
      setError(t('editUser.failedToLoadUser'));
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgsData = orgsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadPrograms = async () => {
    try {
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];
      setPrograms(programsData);
    } catch (error) {
      console.error('Error loading programs:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groupsData = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Group[];
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const loadUserPrograms = async (userId: string): Promise<string[]> => {
    try {
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const userPrograms = programsSnapshot.docs
        .filter(doc => doc.data().managers?.includes(userId))
        .map(doc => doc.id);
      return userPrograms;
    } catch (error) {
      console.error('Error loading user programs:', error);
      return [];
    }
  };

  const loadUserGroups = async (userId: string): Promise<string[]> => {
    try {
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const userGroups = groupsSnapshot.docs
        .filter(doc => doc.data().managers?.includes(userId))
        .map(doc => doc.id);
      return userGroups;
    } catch (error) {
      console.error('Error loading user groups:', error);
      return [];
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      setError(t('editUser.fillRequiredFields'));
      return;
    }

    // For super admin editing: only allow changing system role, not organization
    if (isSuperAdmin(currentUser)) {
      if (formData.systemRole === 'user' && !userToEditData?.organizationId) {
        setError(t('editUser.organizationRequired'));
        return;
      }
    } else {
      // For non-super admin editing: keep existing validation
      if (formData.systemRole === 'user' && !formData.organizationId) {
        setError(t('editUser.organizationRequired'));
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      
      // For super admin: only update system role, preserve existing organization data
      if (isSuperAdmin(currentUser)) {
        const roleToUpdate = formData.systemRole === 'super_admin' 
          ? 'super_admin' 
          : (userToEditData?.organizationRole || 'admin');
        
        // Preserve existing organizationId when changing to user, or clear it when changing to super_admin
        const organizationIdToUpdate = formData.systemRole === 'super_admin' 
          ? undefined 
          : (userToEditData?.organizationId || formData.organizationId);
        
        // If changing to super_admin, unassign from programs and groups first
        if (formData.systemRole === 'super_admin') {
          await unassignUserFromAllEntities(userId, userToEditData?.name || formData.name);
        }
        
        // Update user role
        await updateUserRole(
          userId,
          roleToUpdate,
          formData.name,
          formData.email,
          organizationIdToUpdate
        );
        
        // If changing to super_admin, clear programId, groupId, and organizationId from user document
        // Use deleteField() which is safe even if fields don't exist
        if (formData.systemRole === 'super_admin') {
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            programId: deleteField(),
            groupId: deleteField(),
            organizationId: deleteField()
          });
        }
      } else {
        // For non-super admin: update with form data
        const roleToUpdate = formData.systemRole === 'super_admin' 
          ? 'super_admin' 
          : formData.organizationRole;
        
        await updateUserRole(
          userId,
          roleToUpdate,
          formData.name,
          formData.email,
          formData.organizationId
        );

        // If changing to super_admin, unassign from programs and groups
        if (formData.systemRole === 'super_admin') {
          await unassignUserFromAllEntities(userId, userToEditData?.name || formData.name);
          
          // Clear programId and groupId from user document
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            programId: deleteField(),
            groupId: deleteField(),
            organizationId: deleteField()
          });
        } else {
        // Update program assignments (only for non-super admin)
        await updateAssetAssignments(
          'programs',
          formData.assignedPrograms,
          userToEditData?.assignedPrograms || []
        );

        // Update group assignments (only for non-super admin)
        await updateAssetAssignments(
          'groups',
          formData.assignedGroups,
          userToEditData?.assignedGroups || []
        );
        }
      }
      
      setSuccessMessage(t('editUser.userUpdated'));
      setTimeout(() => {
        onUserUpdated();
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || t('editUser.failedToUpdateUser'));
    } finally {
      setSaving(false);
    }
  };

  const updateAssetAssignments = async (
    collectionName: string,
    newAssignments: string[],
    oldAssignments: string[]
  ) => {
    // Find programs/groups to add
    const toAdd = newAssignments.filter(id => !oldAssignments.includes(id));
    // Find programs/groups to remove
    const toRemove = oldAssignments.filter(id => !newAssignments.includes(id));

    // Add user to new programs/groups
    for (const assetId of toAdd) {
      const assetRef = doc(db, collectionName, assetId);
      await updateDoc(assetRef, {
        managers: arrayUnion(userId),
        managerNames: arrayUnion(formData.name)
      });
    }

    // Remove user from old programs/groups
    for (const assetId of toRemove) {
      const assetRef = doc(db, collectionName, assetId);
      await updateDoc(assetRef, {
        managers: arrayRemove(userId),
        managerNames: arrayRemove(userToEditData?.name || formData.name)
      });
    }
  };

  const unassignUserFromAllEntities = async (userId: string, userName: string) => {
    try {
      // Remove from all programs
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      for (const programDoc of programsSnapshot.docs) {
        const programData = programDoc.data();
        if (programData.managers?.includes(userId)) {
          const programRef = doc(db, 'programs', programDoc.id);
          await updateDoc(programRef, {
            managers: arrayRemove(userId),
            managerNames: arrayRemove(userName)
          });
        }
      }

      // Remove from all groups
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      for (const groupDoc of groupsSnapshot.docs) {
        const groupData = groupDoc.data();
        if (groupData.managers?.includes(userId)) {
          const groupRef = doc(db, 'groups', groupDoc.id);
          await updateDoc(groupRef, {
            managers: arrayRemove(userId),
            managerNames: arrayRemove(userName)
          });
        }
      }

      // Remove from organizations' administrators list
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      for (const orgDoc of orgsSnapshot.docs) {
        const orgData = orgDoc.data();
        if (orgData.administrators?.includes(userId)) {
          const orgRef = doc(db, 'organizations', orgDoc.id);
          const adminNames = orgData.administratorNames || [];
          const nameIndex = adminNames.indexOf(userName);
          const updatedNames = nameIndex >= 0 
            ? [...adminNames.slice(0, nameIndex), ...adminNames.slice(nameIndex + 1)]
            : adminNames;
          
          await updateDoc(orgRef, {
            administrators: arrayRemove(userId),
            administratorNames: updatedNames
          });
        }
      }
    } catch (error) {
      console.error('Error unassigning user from entities:', error);
      throw error;
    }
  };

  const handleSendPasswordReset = async () => {
    if (!formData.email) {
      setError(t('editUser.emailRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await sendPasswordResetEmail(auth, formData.email);
      setSuccessMessage(t('editUser.passwordResetSent'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Error sending password reset:', err);
      setError(t('editUser.passwordResetError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>
            {t('editUser.title')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
          >
            ×
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '6px',
            marginBottom: '16px',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '12px',
            backgroundColor: '#efe',
            border: '1px solid #cfc',
            borderRadius: '6px',
            marginBottom: '16px',
            color: '#3c3'
          }}>
            {successMessage}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            {t('editUser.loading')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333',
                fontSize: '14px'
              }}>
                {t('editUser.fullName')}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('editUser.fullNamePlaceholder')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333',
                fontSize: '14px'
              }}>
                {t('editUser.email')}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('editUser.emailPlaceholder')}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* System Role (only for super admin) */}
            {isSuperAdmin(currentUser) && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#333',
                  fontSize: '14px'
                }}>
                  {t('editUser.systemRole')}
                </label>
                <select
                  value={formData.systemRole}
                  onChange={(e) => {
                    const newSystemRole = e.target.value as SystemRole;
                    setFormData({ 
                      ...formData, 
                      systemRole: newSystemRole,
                      // Clear organization-related fields when changing to super_admin
                      organizationId: newSystemRole === 'super_admin' ? '' : formData.organizationId,
                      organizationRole: newSystemRole === 'super_admin' ? 'admin' : formData.organizationRole
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    cursor: 'pointer'
                  }}
                >
                  <option value="super_admin">{t('users.roles.super_admin')}</option>
                  <option value="user">{t('users.roles.user')}</option>
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '24px',
              flexDirection: 'column'
            }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  backgroundColor: saving ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#218838';
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#28a745';
                }}
              >
                {saving ? t('editUser.updating') : t('editUser.updateUser')}
              </button>

              <button
                onClick={handleSendPasswordReset}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  backgroundColor: saving ? '#ccc' : '#007acc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#005a9e';
                }}
                onMouseLeave={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#007acc';
                }}
              >
                {t('editUser.sendPasswordReset')}
              </button>

              <button
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                    e.currentTarget.style.borderColor = '#999';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#ddd';
                  }
                }}
              >
                {t('editUser.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

